import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, Instagram } from "lucide-react";

const EMBED_SDK = "https://www.instagram.com/embed.js";

let sdkPromise;
function loadSdk() {
  if (typeof window === "undefined") return Promise.reject(new Error("Browser required"));
  if (window.instgrm?.Embeds?.process) return Promise.resolve();
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      let script = document.querySelector(`script[src="${EMBED_SDK}"]`);
      const created = !script;
      if (!script) {
        script = document.createElement("script");
        script.src = EMBED_SDK;
        script.async = true;
        script.dataset.symInstagram = "true";
      }
      const onLoad = () => resolve();
      const onError = () => reject(new Error("Instagram embed unavailable"));
      script.addEventListener("load", onLoad, { once: true });
      script.addEventListener("error", onError, { once: true });
      if (window.instgrm?.Embeds?.process) resolve();
      else if (created) document.body.appendChild(script);
      else {
        // Existing SDK may have loaded before this component mounted.
        window.setTimeout(() => {
          if (window.instgrm?.Embeds?.process) resolve();
        }, 50);
      }
    }).catch((error) => {
      sdkPromise = null; // Allow a retry after a transient failure.
      throw error;
    });
  }
  return sdkPromise;
}

/** One Meta-owned public profile/post embed. Iframe navigation belongs to Instagram. */
export default function InstagramEmbed({ permalink, label = "Instagram content", compact = false }) {
  const rootRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let disposed = false;
    const root = rootRef.current;
    if (!root) return undefined;
    setStatus("loading");
    const detect = () => {
      if (!disposed && root.querySelector("iframe")) setStatus("ready");
    };
    const observer = new MutationObserver(detect);
    observer.observe(root, { childList: true, subtree: true });
    loadSdk().then(() => {
      if (!disposed) {
        try { window.instgrm?.Embeds?.process(); }
        catch { setStatus("unavailable"); }
        detect();
      }
    }).catch(() => { if (!disposed) setStatus("unavailable"); });
    const timer = window.setTimeout(() => {
      if (!disposed && !root.querySelector("iframe")) setStatus("unavailable");
    }, 12000);
    return () => {
      disposed = true;
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [permalink]);

  return (
    <div className={`sym-ig-embed-shell ${compact ? "sym-ig-embed-shell--compact" : ""}`}>
      {status === "loading" && <div className="sym-ig-loading" role="status">Loading Instagram preview…</div>}
      <div ref={rootRef} className={status === "unavailable" ? "sym-ig-hidden" : "sym-ig-embed-area"}>
        <blockquote
          key={permalink}
          className="instagram-media"
          data-instgrm-permalink={permalink}
          data-instgrm-version="14"
          style={{ background: "#fff", border: 0, margin: "0 auto", maxWidth: 540, minWidth: 0, width: "100%" }}
        >
          <a href={permalink} target="_blank" rel="noopener noreferrer">View {label} on Instagram</a>
        </blockquote>
      </div>
      {status === "unavailable" && (
        <div className="sym-ig-fallback" role="status">
          <Instagram size={28} aria-hidden="true" />
          <strong>Instagram preview unavailable</strong>
          <p>Instagram may have disabled this embed, or the browser may be blocking it.</p>
          <a href={permalink} target="_blank" rel="noopener noreferrer">View on Instagram <ExternalLink size={15} /></a>
        </div>
      )}
    </div>
  );
}
